import { useCallback, useState } from "react";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from "antd";

import { useNavigate } from "react-router";

import {
  ACCOUNT_PAGE_SIZE_OPTIONS,
  ACCOUNT_STATUS_OPTIONS,
  DEFAULT_ACCOUNT_PAGE_SIZE,
} from "./accounts.constants";

import { PillPagination } from "../../components/PillPagination";
import DataState from "../../components/DataState/DataState";

import {
  useAccountsQuery,
  useAddAccountMutation,
  useDeleteAccountMutation,
  useUpdateAccountMutation,
} from "./accounts.hooks";
import "../../styles/trackon.css";



const { Title, Text } = Typography;

const tableWrapStyle = {
  overflowX: "auto",
  minHeight: 80,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  background: "#ffffff",
};

const thStyle = {
  border: "1px solid #e6e6e6",
  padding: "8px 10px",
  background: "#f5f5f5",
  fontWeight: 600,
  fontSize: 12,
  color: "#595959",
  textAlign: "left",
};

const tdStyle = {
  border: "1px solid #e6e6e6",
  padding: "6px 10px",
  verticalAlign: "middle",
  fontSize: 13,
};

export function AccountsPage() {
  const navigate = useNavigate();

  // --------------------------------------------------
  // Modal state
  // --------------------------------------------------
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // --------------------------------------------------
  // Selected/editing row
  // --------------------------------------------------
  const [editingRow, setEditingRow] = useState(null);

  // --------------------------------------------------
  // Delete state
  // --------------------------------------------------
  const [deletingId, setDeletingId] = useState(null);

  // --------------------------------------------------
  // Backend error state
  // --------------------------------------------------
  const [addAccountError, setAddAccountError] = useState(
    null,
  );

  const [editAccountError, setEditAccountError] = useState(
    null,
  );

  // --------------------------------------------------
  // Pagination
  // --------------------------------------------------
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_ACCOUNT_PAGE_SIZE,
  });

  // --------------------------------------------------
  // Forms
  // --------------------------------------------------
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // --------------------------------------------------
  // Queries / Mutations
  // --------------------------------------------------
  const accountsQuery = useAccountsQuery(
    pagination.current,
    pagination.pageSize,
  );

  const addAccountMutation = useAddAccountMutation();
  const updateAccountMutation = useUpdateAccountMutation();
  const deleteAccountMutation = useDeleteAccountMutation();

  // --------------------------------------------------
  // Table data
  // --------------------------------------------------
  const rows = accountsQuery.data?.rows ?? [];
  const totalAccounts = accountsQuery.data?.total ?? 0;

  // keepPreviousData keeps the last page visible during a page change, so loading
  // also covers placeholder data, a retry after an error, and a fetch with no data.
  const status =
    accountsQuery.isFetching &&
    (accountsQuery.isError || accountsQuery.isPlaceholderData || !accountsQuery.data)
      ? "loading"
      : accountsQuery.isError
        ? "error"
        : "ready";

  const loading = status === "loading";

  // Load failures are shown by <DataState /> as the error view (no toast).
  const loadErrorMessage =
    accountsQuery.error instanceof Error
      ? accountsQuery.error.message
      : "Something went wrong. Please try again.";

  // --------------------------------------------------
  // Open Add Account Modal
  // --------------------------------------------------
  const openAddModal = useCallback(() => {
    setAddAccountError(null);
    addForm.resetFields();

    setAddModalOpen(true);
  }, [addForm]);

  // --------------------------------------------------
  // Close Add Account Modal
  // --------------------------------------------------
  const closeAddModal = useCallback(() => {
    setAddModalOpen(false);
    setAddAccountError(null);

    addForm.resetFields();
  }, [addForm]);

  // --------------------------------------------------
  // Open Edit Account Modal
  // --------------------------------------------------
  const openEditModal = useCallback(
    (record) => {
      setEditAccountError(null);

      setEditingRow(record);

      editForm.setFieldsValue({
        accountName: record.name,
        status: record.status,
      });

      setEditModalOpen(true);
    },
    [editForm],
  );

  // --------------------------------------------------
  // Close Edit Account Modal
  // --------------------------------------------------
  const closeEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditAccountError(null);
    setEditingRow(null);

    editForm.resetFields();
  }, [editForm]);

  // --------------------------------------------------
  // Add Account
  // --------------------------------------------------
  const handleAddAccount = useCallback(async () => {
    try {
      const values = await addForm.validateFields();

      // Clear previous backend error
      setAddAccountError(null);

      await addAccountMutation.mutateAsync(values);

      // Success
      closeAddModal();
    } catch (error) {
      // Backend/API error
      if (error instanceof Error) {
        setAddAccountError(error.message);
      } else {
        setAddAccountError("Failed to create account.");
      }
    }
  }, [
    addAccountMutation,
    addForm,
    closeAddModal,
  ]);

  // --------------------------------------------------
  // Edit Account
  // --------------------------------------------------
  const handleEditAccount = useCallback(async () => {
    if (!editingRow) {
      return;
    }

    try {
      const values = await editForm.validateFields();

      // Clear previous backend error
      setEditAccountError(null);

      await updateAccountMutation.mutateAsync({
        accountId: editingRow.id,
        payload: values,
      });

      // Success
      closeEditModal();
    } catch (error) {
      // Backend/API error
      if (error instanceof Error) {
        setEditAccountError(error.message);
      } else {
        setEditAccountError("Failed to update account.");
      }
    }
  }, [
    closeEditModal,
    editForm,
    editingRow,
    updateAccountMutation,
  ]);

  // --------------------------------------------------
  // Delete Account
  // --------------------------------------------------
  const handleDelete = useCallback(
    async (record) => {
      setDeletingId(record.id);

      try {
        await deleteAccountMutation.mutateAsync(record.id);

        const remainingOnPage = rows.length - 1;

        const shouldMoveToPreviousPage =
          remainingOnPage <= 0 &&
          pagination.current > 1;

        if (shouldMoveToPreviousPage) {
          setPagination((prev) => ({
            ...prev,
            current: prev.current - 1,
          }));
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Unable to delete account.";

        message.error(errorMessage);
      } finally {
        setDeletingId(null);
      }
    },
    [
      deleteAccountMutation,
      pagination.current,
      rows.length,
    ],
  );

  // --------------------------------------------------
  // Table Pagination
  // --------------------------------------------------
  const handleTableChange = useCallback(
    (current, pageSize) => {
      setPagination((prev) => {
        const nextPageSize =
          pageSize ?? prev.pageSize;

        // Reset to page 1 when page size changes
        if (nextPageSize !== prev.pageSize) {
          return {
            current: 1,
            pageSize: nextPageSize,
          };
        }

        return {
          current,
          pageSize: nextPageSize,
        };
      });
    },
    [],
  );

  // --------------------------------------------------
  // Table Columns
  // --------------------------------------------------
  // --------------------------------------------------
  // Page
  // --------------------------------------------------
  return (
    <div>
      {/* Header */}
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() =>
                navigate("/watchtower/settings")
              }
            />

            <Title
              level={4}
              style={{ margin: 0 }}
            >
              Accounts Management
            </Title>
          </div>
        </div>

        {status === "ready" && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openAddModal}
        >
          Add Account
        </Button>
        )}
      </div>

      {/* Accounts Table */}
      <DataState
        status={status}
        errorTitle="Couldn't load accounts"
        error={loadErrorMessage}
        onRetry={() => {
          void accountsQuery.refetch();
        }}
        skeletonRows={6}
      >
      <Card bordered={false}>
        <div style={{ ...tableWrapStyle, maxWidth: 720 }}>
          <>
          <Spin spinning={loading}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Account</th>
                  <th style={{ ...thStyle, width: 120 }}>Status</th>
                  <th style={{ ...thStyle, width: 90, textAlign: "center" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td style={tdStyle} colSpan={3}>
                      <Text style={{ color: "#8c8c8c", fontSize: 13 }}>
                        No accounts found.
                      </Text>
                    </td>
                  </tr>
                ) : (
                  rows.map((record) => (
                    <tr key={record.id}>
                      <td style={tdStyle}>{record.name}</td>
                      <td style={tdStyle}>{record.status ? "Active" : "Inactive"}</td>
                      <td style={{ ...tdStyle, width: 90, textAlign: "center" }}>
                        <Space size={12}>
                          <EditOutlinedIcon
                            onClick={() => openEditModal(record)}
                            style={{
                              fontSize: 17,
                              cursor: "pointer",
                              color: "#595959",
                            }}
                          />

                          {record.status && (
                            <Popconfirm
                            overlayClassName="trackon-root"
                              title="Delete account"
                              description="Are you sure you want to delete this account?"
                              okText="Delete"
                              okButtonProps={{
                                danger: true,
                                loading: deletingId === record.id,
                              }}
                              cancelText="Cancel"
                              onConfirm={() => {
                                void handleDelete(record);
                              }}
                            >
                              <DeleteOutlined
                                style={{
                                  fontSize: 16,
                                  cursor: "pointer",
                                  color: "#ff4d4f",
                                  opacity: deletingId === record.id ? 0.5 : 1,
                                }}
                              />
                            </Popconfirm>
                          )}
                        </Space>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Spin>

          <PillPagination
            currentPage={pagination.current}
            pageSize={pagination.pageSize}
            total={totalAccounts}
            itemLabel="accounts"
            onPageChange={(page) => handleTableChange(page, pagination.pageSize)}
            pageSizeOptions={[...ACCOUNT_PAGE_SIZE_OPTIONS]}
            onPageSizeChange={(size) => handleTableChange(1, size)}
          />
          </>
        </div>
      </Card>
      </DataState>

      {/* =========================================
          Add Account Modal
          ========================================= */}
      <Modal
        title="Add Account"
        open={addModalOpen}
        onOk={() => {
          void handleAddAccount();
        }}
        okText="Create"
        cancelText="Cancel"
        confirmLoading={
          addAccountMutation.isPending
        }
        onCancel={closeAddModal}
      >
        {/* Backend Error */}
        {addAccountError && (
          <Alert
            type="error"
            showIcon
            message={addAccountError}
            closable
            onClose={() =>
              setAddAccountError(null)
            }
            style={{
              marginBottom: 16,
            }}
          />
        )}

        <Form
          form={addForm}
          layout="vertical"
          requiredMark={false}
          initialValues={{
            accountName: "",
            status: true,
          }}
        >
          <Form.Item
            label="Account Name"
            name="accountName"
            rules={[
              {
                required: true,
                message:
                  "Please enter account name.",
              },
              {
                whitespace: true,
                message:
                  "Account name cannot be empty.",
              },
            ]}
          >
            <Input
              placeholder="Enter account name"
              maxLength={120}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* =========================================
          Edit Account Modal
          ========================================= */}
      <Modal
      className="trackon-root"
        title="Edit Account"
        open={editModalOpen}
        onOk={() => {
          void handleEditAccount();
        }}
        okText="Save"
        cancelText="Cancel"
        confirmLoading={
          updateAccountMutation.isPending
        }
        onCancel={closeEditModal}
      >
        {/* Backend Error */}
        {editAccountError && (
          <Alert
            type="error"
            showIcon
            message={editAccountError}
            closable
            onClose={() =>
              setEditAccountError(null)
            }
            style={{
              marginBottom: 16,
            }}
          />
        )}

        <Form
          form={editForm}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            label="Account Name"
            name="accountName"
            rules={[
              {
                required: true,
                message:
                  "Please enter account name.",
              },
              {
                whitespace: true,
                message:
                  "Account name cannot be empty.",
              },
            ]}
          >
            <Input
              placeholder="Enter account name"
              maxLength={120}
            />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[
              {
                required: true,
                message:
                  "Please select status.",
              },
            ]}
          >
            <Select
              options={[
                ...ACCOUNT_STATUS_OPTIONS,
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}